import NotificationBell from './NotificationBell';
import SettingsIcon from '@mui/icons-material/Settings';
import DevicesIcon from '@mui/icons-material/Devices';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Box, List, ListItem, ListItemButton, ListItemIcon,
  ListItemText, Typography, Avatar, Divider, Drawer,
  IconButton, useMediaQuery, useTheme
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BarChartIcon from '@mui/icons-material/BarChart';
import LogoutIcon from '@mui/icons-material/Logout';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CampaignIcon from '@mui/icons-material/Campaign';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

export const SIDEBAR_WIDTH = 230;

const SIDEBAR_BG = '#1a4d2e';
const SIDEBAR_ACTIVE = '#2d7a4f';
const SIDEBAR_TEXT = 'rgba(255,255,255,0.75)';
const SIDEBAR_TEXT_ACTIVE = '#ffffff';
const SIDEBAR_DIVIDER = 'rgba(255,255,255,0.1)';

const employeeNavItems = [
  { label: 'Dashboard', icon: <DashboardIcon fontSize="small" />, path: '/dashboard' },
  { label: 'My Attendance', icon: <AccessTimeIcon fontSize="small" />, path: '/dashboard?tab=attendance' },
  { label: 'Summary', icon: <BarChartIcon fontSize="small" />, path: '/summary' },
  { label: 'Announcements', icon: <CampaignIcon fontSize="small" />, path: '/announcements' },
  { label: 'Absence Requests', icon: <EventBusyIcon fontSize="small" />, path: '/absence' },
   { label: 'My Profile', icon: <PersonIcon fontSize="small" />, path: '/profile' },
];

const adminNavItems = [
  { label: 'Dashboard', icon: <DashboardIcon fontSize="small" />, path: '/admin' },
  { label: 'All Attendance', icon: <AccessTimeIcon fontSize="small" />, path: '/admin?tab=attendance' },
  { label: 'Summary', icon: <BarChartIcon fontSize="small" />, path: '/summary' },
  { label: 'Announcements', icon: <CampaignIcon fontSize="small" />, path: '/announcements' },
  { label: 'Absence Requests', icon: <EventBusyIcon fontSize="small" />, path: '/absence' },
  { label: 'Manage Users', icon: <PeopleIcon fontSize="small" />, path: '/admin?tab=users' },
  { label: 'Device Registrations', icon: <DevicesIcon fontSize="small" />, path: '/devices' },
  { label: 'Geofence', icon: <LocationOnIcon fontSize="small" />, path: '/admin?tab=geofence' },
  { label: 'Settings', icon: <SettingsIcon fontSize="small" />, path: '/settings' },
  { label: 'My Profile', icon: <PersonIcon fontSize="small" />, path: '/profile' },
  { label: 'Reports', icon: <BarChartIcon fontSize="small" />, path: '/admin?tab=reports' },
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = user?.role === 'admin' ? adminNavItems : employeeNavItems;

  const isActive = (path) => {
    const basePath = path.split('?')[0];
    const tabParam = path.includes('?tab=') ? path.split('?tab=')[1] : null;
    const currentTab = new URLSearchParams(location.search).get('tab');
    if (tabParam) return location.pathname === basePath && currentTab === tabParam;
    return location.pathname === basePath && !currentTab;
  };

  const handleNav = (path) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || user.username?.[0]?.toUpperCase()
    : 'U';

  return (
    <Box sx={{
      width: SIDEBAR_WIDTH,
      minHeight: '100vh',
      background: SIDEBAR_BG,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Logo */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${SIDEBAR_DIVIDER}` }}>
        
        <img
          src="/logo.png"
          alt="NCAOOSCE Logo"
          style={{ 
            width: '180px', 
            height: 'auto', 
            display: 'block',
          }}
        />
      
    </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, py: 1.5, px: 1, overflowY: 'auto' }}>
        <Typography sx={{
          fontSize: 10, color: 'rgba(255,255,255,0.35)',
          textTransform: 'uppercase', letterSpacing: '0.08em',
          px: 1, py: 0.5, mb: 0.5,
        }}>
          {user?.role === 'admin' ? 'Admin Menu' : 'Menu'}
        </Typography>
        <List dense disablePadding>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => handleNav(item.path)}
                sx={{
                  borderRadius: 2, py: 1, px: 1.25,
                  background: isActive(item.path) ? SIDEBAR_ACTIVE : 'transparent',
                  color: isActive(item.path) ? SIDEBAR_TEXT_ACTIVE : SIDEBAR_TEXT,
                  '&:hover': {
                    background: isActive(item.path) ? SIDEBAR_ACTIVE : 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: isActive(item.path) ? 'white' : SIDEBAR_TEXT }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontSize: 13, fontWeight: isActive(item.path) ? 500 : 400 }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* User + Logout */}
      
      <Box sx={{ borderTop: `1px solid ${SIDEBAR_DIVIDER}`, p: 1.5 }}>

        {/* Notification bell */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <NotificationBell />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Avatar sx={{ width: 32, height: 32, background: SIDEBAR_ACTIVE, fontSize: 12, fontWeight: 500 }}>
            {initials}
          </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: 'white', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>
            {user?.role === 'admin' ? 'Administrator' : user?.role === 'department_head' ? 'Dept. Head' : user?.department || 'Employee'}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{ borderColor: SIDEBAR_DIVIDER, mb: 1 }} />
      <ListItemButton
        onClick={handleLogout}
        sx={{
          borderRadius: 2, py: 0.75, px: 1.25,
          color: 'rgba(255,255,255,0.6)',
          '&:hover': { background: 'rgba(255,0,0,0.1)', color: '#ff8a80' },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Sign Out" primaryTypographyProps={{ fontSize: 13 }} />
      </ListItemButton>
    </Box>
    </Box>
  );
}

export default function Sidebar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  if (isMobile) {
    return (
      <>
        {/* Mobile hamburger button — fixed top left */}
        <Box sx={{
          position: 'fixed',
          top: 12,
          left: 12,
          zIndex: 1200,
          background: '#1a4d2e',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        }}>
          <IconButton
            onClick={() => setMobileOpen(true)}
            sx={{ color: 'white', p: 1 }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Mobile drawer */}
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: SIDEBAR_WIDTH,
              border: 'none',
              background: SIDEBAR_BG,
            }
          }}
        >
          {/* Close button inside drawer */}
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
            <IconButton
              onClick={() => setMobileOpen(false)}
              sx={{ color: 'rgba(255,255,255,0.6)', p: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </Drawer>
      </>
    );
  }

  // Desktop — fixed sidebar
  return (
    <Box sx={{
      width: SIDEBAR_WIDTH,
      minHeight: '100vh',
      background: SIDEBAR_BG,
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      top: 0,
      left: 0,
      bottom: 0,
      zIndex: 100,
      overflowY: 'auto',
      overflowX: 'hidden',
    }}>
      <SidebarContent />
    </Box>
  );
}